import React, { useState, useEffect } from 'react';
import { Table, TableBody, Text } from '@bambuser-ui';
import { Autocomplete, Icon } from '@livecommerce/design-system';
import TableRowUser from 'components/TableRowUser.jsx';
import { useLocalization } from 'contexts/LocalizationContext.jsx';
import { compose } from 'recompose';
import { withFirebase } from 'components/Firebase/index.js';
import { withAuthorization } from 'components/Session/index.js';
import useOrgId from 'hooks/useOrgId.js';
import UserInfo from 'components/UserInfo.jsx';
import { sort } from 'helpers/sort.js';

const LinkResource = ({ firebase, saveCalendarId, calendarId }) => {
  const { t } = useLocalization();
  const orgId = useOrgId();
  const [isLoading, setIsLoading] = useState(true);
  const [resources, setResources] = useState();
  const [linkedResource, setLinkedResource] = useState(null);

  useEffect(() => {
    getResourcesAndSetLinkedResource();
  }, [calendarId]);

  const getResourcesAndSetLinkedResource = async () => {
    try {
      if (resources === undefined) {
        const resources = await getResources();
        setResources(resources);
        setIsLoading(false);
        findAndSetLinkedResource(resources, calendarId);
      } else {
        findAndSetLinkedResource(resources, calendarId);
      }
    } catch (e) {
      console.error('Failed to fetch or link resources.', e.message);
      setIsLoading(false);
    }
  };

  const getResources = async () => {
    const apiClient = await firebase.getAgent();
    const result = await apiClient.get(`/bookings/appointedd/resources?orgId=${orgId}`);
    return result.body.data.map(userFromAppointeddResource);
  };

  const findAndSetLinkedResource = (resources, calendarId) => {
    let linkedResource;

    if (calendarId) {
      linkedResource = resources.find((resource) => {
        return resource.id === calendarId;
      });

      if (!linkedResource) {
        throw new Error(
          `A resource with calendarId ${calendarId} could not be found in the external booking system. Number of resources: ${resources.length}`,
        );
      }
    }
    setLinkedResource(linkedResource);
  };

  const userFromAppointeddResource = (resource) => ({
    displayName: resource.profile.name,
    avatar: {
      url: resource.image_url,
    },
    id: resource.id,
    email: resource.profile.email,
  });

  const handleResourceSelected = (resource) => {
    const resourceId = resource?.selectedItem?.label?.props?.user?.id;
    saveCalendarId(resourceId);
    findAndSetLinkedResource(resources, resourceId);
  };

  const unlinkResource = async () => {
    try {
      saveCalendarId(null);
    } catch (error) {
      console.error('Error deleting calendarId from database: ', JSON.stringify(error));
    }
  };

  const mapItem = (resource) => {
    const customElement = <UserInfo user={resource} attribute="email" />;
    return {
      value: resource.displayName,
      label: resource.displayName,
      customElement,
    };
  };

  let render;

  if (isLoading) {
    render = null;
  } else if (linkedResource) {
    render = (
      <Table>
        <TableBody>
          <TableRowUser
            user={linkedResource}
            attribute="email"
            buttonIcon={<Icon name="cross" height={12} width={12} />}
            onClick={unlinkResource}
          />
        </TableBody>
      </Table>
    );
  } else if (Array.isArray(resources)) {
    if (resources.length > 0) {
      const items = sort(resources, 'displayName').map(mapItem);
      render = [(
        <Autocomplete
          id="link-resource"
          items={items}
          placeholder="Choose"
          onSelectedItemChange={handleResourceSelected}
          key="0"
        />
      )];

      if (calendarId && !linkedResource) {
        render.push((
          <Text style={styles.failed} type="meta" key="1">
            {t('linkResource.failedToLink')}
          </Text>
        ));
      }
    } else {
      render = (
        <Text style={styles.noResources} type="meta">
          {t('linkResource.noResources')}
        </Text>
      );
    }
  } else {
    render = (
      <Text style={styles.failed} type="meta">
        {t('linkResource.failedToFetch')}
      </Text>
    );
  }

  return render;
};

const styles = {
  failed: {
    textAlign: 'center',
    color: 'red',
  },
  noResources: {
    textAlign: 'center',
  },
};

export default compose(
  withFirebase,
  withAuthorization((authUser) => !!authUser),
)(LinkResource);
